/**
 * Enhanced auth state persistence utilities
 * Handles serialization/deserialization of complex state structures including Sets and Maps
 */

// Types - using type-only imports to avoid circular dependencies
type AuthPhase =
  | 'idle'
  | 'contact'
  | 'otp'
  | 'password'
  | 'setPassword'
  | 'alternateContact'
  | 'accountDiscovery'
  | 'identityConflict'
  | 'identityLinkingOtp'
  | 'generalProfile'
  | 'teacherProfile'
  | 'studentProfile'
  | 'parentProfile'
  | 'employerProfile'
  | 'schoolProfile'
  | 'organizerProfile'
  | 'complete'
  | 'error';

// Storage keys
export const AUTH_STATE_STORAGE_KEYS = {
  ENHANCED_AUTH_STATE: 'enhanced_auth_state',
  AUTH_STATE_TIMESTAMP: 'auth_state_timestamp',
  STATE_VERSION: 'auth_state_version'
} as const;

// Current state version for future migrations
const CURRENT_STATE_VERSION = '1.0.0';

// Storage expiration time (24 hours in milliseconds)
const STORAGE_EXPIRATION_TIME = 24 * 60 * 60 * 1000;

/**
 * Enhanced auth state structure for persistence
 */
export interface SerializableAuthState {
  version: string;
  timestamp: number;
  phase: AuthPhase;
  phaseHistory: AuthPhase[];
  completedPhases: AuthPhase[];
  maxReachedPhase: AuthPhase;

  // Enhanced state tracking (serialized from Sets/Maps)
  encounteredPhases: AuthPhase[];
  criticalDecisions: Record<string, any>;
  lockedPhases: AuthPhase[];
  conditionalTriggers: Record<string, any>;
  passedGates: AuthPhase[];
  resolvedConflicts: string[];

  // Profile data with proper role structure
  profileData: {
    general?: any;
    teacher?: any;
    student?: any;
    parent?: any;
    employer?: any;
    school?: any;
    organizer?: any;
  };

  // Contact information
  contact: string | null;
  isEmail: boolean;
  selectedUserTypes: string[];
  currentRoleIndex: number;

  // Identity linking state (non-sensitive parts only)
  pendingIdentityLink: {
    type: string | null;
    value: string | null;
    conflictingUserId: string | null;
  };

  // Additional tracking
  alternateContactSkipped: boolean;
  discoveredAccountId: string | null;
  portfolioCompletionStatus: any | null;
  previousPhase: AuthPhase;

  // Redirect target (e.g. /guide/{id}/0) — must survive page reloads
  originalRedirect: string | null;
}

/**
 * Serializes auth state to JSON-compatible format
 * Converts Sets to Arrays and Maps to Objects
 */
export const serializeAuthState = (state: any): string => {
  try {
    const serializable: SerializableAuthState = {
      version: CURRENT_STATE_VERSION,
      timestamp: Date.now(),
      phase: state.phase,
      phaseHistory: Array.from(state.phaseHistory || []),
      completedPhases: Array.from(state.completedPhases || []),
      maxReachedPhase: state.maxReachedPhase,

      // Convert Sets to Arrays
      encounteredPhases: Array.from(state.encounteredPhases || new Set()),
      lockedPhases: Array.from(state.lockedPhases || new Set()),
      passedGates: Array.from(state.passedGates || new Set()),
      resolvedConflicts: Array.from(state.resolvedConflicts || new Set()),

      // Convert Maps to Objects
      criticalDecisions: state.criticalDecisions ? Object.fromEntries(state.criticalDecisions) : {},
      conditionalTriggers: state.conditionalTriggers
        ? Object.fromEntries(state.conditionalTriggers)
        : {},

      // Profile data (deep clone to avoid mutation)
      profileData: {
        general: state.profileData?.general
          ? JSON.parse(JSON.stringify(state.profileData.general))
          : undefined,
        teacher: state.profileData?.teacher
          ? JSON.parse(JSON.stringify(state.profileData.teacher))
          : undefined,
        student: state.profileData?.student
          ? JSON.parse(JSON.stringify(state.profileData.student))
          : undefined,
        parent: state.profileData?.parent
          ? JSON.parse(JSON.stringify(state.profileData.parent))
          : undefined,
        employer: state.profileData?.employer
          ? JSON.parse(JSON.stringify(state.profileData.employer))
          : undefined,
        school: state.profileData?.school
          ? JSON.parse(JSON.stringify(state.profileData.school))
          : undefined,
        organizer: state.profileData?.organizer
          ? JSON.parse(JSON.stringify(state.profileData.organizer))
          : undefined
      },

      // Contact information
      contact: state.contact,
      isEmail: state.isEmail,
      selectedUserTypes: Array.from(state.selectedUserTypes || []),
      currentRoleIndex: state.currentRoleIndex || 0,

      // Identity linking (only non-sensitive data)
      pendingIdentityLink: {
        type: state.pendingIdentityLink?.type || null,
        value: state.pendingIdentityLink?.value || null,
        conflictingUserId: state.pendingIdentityLink?.conflictingUserId || null
      },

      // Additional tracking
      alternateContactSkipped: state.alternateContactSkipped || false,
      discoveredAccountId: state.discoveredAccountId || null,
      portfolioCompletionStatus: state.portfolioCompletionStatus || null,
      previousPhase: state.previousPhase || 'idle',

      // Redirect target — must survive page reloads (e.g. /guide/{id}/0)
      originalRedirect: state.originalRedirect || null
    };

    return JSON.stringify(serializable);
  } catch (error) {
    console.error('[AuthStatePersistence] Serialization failed:', error);
    throw new Error('Failed to serialize auth state');
  }
};

/**
 * Deserializes auth state from JSON format
 * Converts Arrays back to Sets and Objects back to Maps
 */
export const deserializeAuthState = (serializedState: string): any => {
  try {
    const parsed: SerializableAuthState = JSON.parse(serializedState);

    // Check version compatibility
    if (parsed.version !== CURRENT_STATE_VERSION) {
      console.warn(
        '[AuthStatePersistence] State version mismatch:',
        parsed.version,
        'expected:',
        CURRENT_STATE_VERSION
      );
      // For now, continue - add migration logic here in future
    }

    // Check expiration
    const age = Date.now() - parsed.timestamp;
    if (age > STORAGE_EXPIRATION_TIME) {
      throw new Error('Stored auth state has expired');
    }

    // Reconstruct state with proper data structures
    const state = {
      phase: parsed.phase,
      phaseHistory: parsed.phaseHistory || [],
      completedPhases: new Set(parsed.completedPhases || []),
      maxReachedPhase: parsed.maxReachedPhase,

      // Convert Arrays back to Sets
      encounteredPhases: new Set(parsed.encounteredPhases || []),
      lockedPhases: new Set(parsed.lockedPhases || []),
      passedGates: new Set(parsed.passedGates || []),
      resolvedConflicts: new Set(parsed.resolvedConflicts || []),

      // Convert Objects back to Maps
      criticalDecisions: new Map(Object.entries(parsed.criticalDecisions || {})),
      conditionalTriggers: new Map(Object.entries(parsed.conditionalTriggers || {})),

      // Profile data with proper role structure
      profileData: {
        general: parsed.profileData?.general,
        teacher: parsed.profileData?.teacher,
        student: parsed.profileData?.student,
        parent: parsed.profileData?.parent,
        employer: parsed.profileData?.employer,
        school: parsed.profileData?.school,
        organizer: parsed.profileData?.organizer
      },

      // Contact information
      contact: parsed.contact,
      isEmail: parsed.isEmail,
      selectedUserTypes: parsed.selectedUserTypes || [],
      currentRoleIndex: parsed.currentRoleIndex || 0,

      // Identity linking
      pendingIdentityLink: {
        type: parsed.pendingIdentityLink?.type || null,
        value: parsed.pendingIdentityLink?.value || null,
        conflictingUserId: parsed.pendingIdentityLink?.conflictingUserId || null
      },

      // Additional tracking
      alternateContactSkipped: parsed.alternateContactSkipped || false,
      discoveredAccountId: parsed.discoveredAccountId || null,
      portfolioCompletionStatus: parsed.portfolioCompletionStatus || null,
      previousPhase: parsed.previousPhase || 'idle',

      // Redirect target (restored from persistence)
      originalRedirect: parsed.originalRedirect || null,

      // Runtime state (not persisted)
      loading: false,
      error: null,
      showDialog: false
    };

    return state;
  } catch (error) {
    console.error('[AuthStatePersistence] Deserialization failed:', error);
    throw error;
  }
};

/**
 * Saves enhanced auth state to localStorage with incremental updates
 */
export const saveEnhancedAuthState = (state: any): boolean => {
  try {
    const serialized = serializeAuthState(state);
    localStorage.setItem(AUTH_STATE_STORAGE_KEYS.ENHANCED_AUTH_STATE, serialized);
    localStorage.setItem(AUTH_STATE_STORAGE_KEYS.AUTH_STATE_TIMESTAMP, Date.now().toString());
    localStorage.setItem(AUTH_STATE_STORAGE_KEYS.STATE_VERSION, CURRENT_STATE_VERSION);

    console.log('[AuthStatePersistence] Enhanced state saved successfully');
    return true;
  } catch (error) {
    console.error('[AuthStatePersistence] Failed to save enhanced state:', error);
    return false;
  }
};

/**
 * Clears enhanced auth state from localStorage
 */
export const clearEnhancedAuthState = (): void => {
  try {
    localStorage.removeItem(AUTH_STATE_STORAGE_KEYS.ENHANCED_AUTH_STATE);
    localStorage.removeItem(AUTH_STATE_STORAGE_KEYS.AUTH_STATE_TIMESTAMP);
    localStorage.removeItem(AUTH_STATE_STORAGE_KEYS.STATE_VERSION);
    console.log('[AuthStatePersistence] Enhanced state cleared');
  } catch (error) {
    console.error('[AuthStatePersistence] Failed to clear enhanced state:', error);
  }
};

/**
 * Loads enhanced auth state from localStorage with validation
 */
export const loadEnhancedAuthState = (): any | null => {
  try {
    const serialized = localStorage.getItem(AUTH_STATE_STORAGE_KEYS.ENHANCED_AUTH_STATE);
    if (!serialized) {
      console.log('[AuthStatePersistence] No saved state found');
      return null;
    }

    const state = deserializeAuthState(serialized);
    console.log('[AuthStatePersistence] Enhanced state loaded successfully');
    return state;
  } catch (error) {
    console.error('[AuthStatePersistence] Failed to load enhanced state:', error);
    clearEnhancedAuthState();
    return null;
  }
};

/**
 * Validates the integrity of loaded auth state
 */
export const validateAuthState = (state: any): boolean => {
  try {
    // Check required properties
    const requiredProperties = [
      'phase',
      'phaseHistory',
      'completedPhases',
      'encounteredPhases',
      'conditionalTriggers'
    ];

    // Use forEach instead of for...of to avoid iterator issues
    const missingProps = requiredProperties.filter(prop => !(prop in state));
    if (missingProps.length > 0) {
      console.warn('[AuthStatePersistence] Missing required properties:', missingProps);
      return false;
    }

    // Check data structure types
    if (!(state.completedPhases instanceof Set)) {
      console.warn('[AuthStatePersistence] completedPhases is not a Set');
      return false;
    }

    if (!(state.encounteredPhases instanceof Set)) {
      console.warn('[AuthStatePersistence] encounteredPhases is not a Set');
      return false;
    }

    if (!(state.passedGates instanceof Set)) {
      console.warn('[AuthStatePersistence] passedGates is not a Set');
      return false;
    }

    if (!(state.resolvedConflicts instanceof Set)) {
      console.warn('[AuthStatePersistence] resolvedConflicts is not a Set');
      return false;
    }

    if (!(state.criticalDecisions instanceof Map)) {
      console.warn('[AuthStatePersistence] criticalDecisions is not a Map');
      return false;
    }

    if (!(state.conditionalTriggers instanceof Map)) {
      console.warn('[AuthStatePersistence] conditionalTriggers is not a Map');
      return false;
    }

    // Check phase validity
    const validPhases = [
      'idle',
      'contact',
      'otp',
      'password',
      'setPassword',
      'alternateContact',
      'accountDiscovery',
      'identityConflict',
      'identityLinkingOtp',
      'generalProfile',
      'teacherProfile',
      'studentProfile',
      'parentProfile',
      'employerProfile',
      'schoolProfile',
      'organizerProfile',
      'complete',
      'error'
    ];

    if (!validPhases.includes(state.phase)) {
      console.warn('[AuthStatePersistence] Invalid phase:', state.phase);
      return false;
    }

    console.log('[AuthStatePersistence] State validation passed');
    return true;
  } catch (error) {
    console.error('[AuthStatePersistence] State validation failed:', error);
    return false;
  }
};

/**
 * Performs incremental state update (only changed properties)
 */
export const updateEnhancedAuthState = (stateUpdates: Partial<any>): boolean => {
  try {
    const currentState = loadEnhancedAuthState();
    if (!currentState) {
      console.log('[AuthStatePersistence] No current state, performing full save');
      return false;
    }

    // Merge updates with current state
    const updatedState = { ...currentState, ...stateUpdates };
    return saveEnhancedAuthState(updatedState);
  } catch (error) {
    console.error('[AuthStatePersistence] Incremental update failed:', error);
    return false;
  }
};

/**
 * Gets the age of stored state in milliseconds
 */
export const getStoredStateAge = (): number | null => {
  try {
    const timestamp = localStorage.getItem(AUTH_STATE_STORAGE_KEYS.AUTH_STATE_TIMESTAMP);
    if (!timestamp) return null;

    return Date.now() - parseInt(timestamp, 10);
  } catch (error) {
    console.error('[AuthStatePersistence] Failed to get state age:', error);
    return null;
  }
};
